import React, { Fragment, ReactNode } from 'react';
import classNames from 'classnames';
import { Dialog, Transition } from '@headlessui/react';

import { Nullable } from 'src/types/nullable.type';
import SlideOverCloseButton from './close-button';
import { LoaderStrongIcon } from 'src/monet/icons';
import LoadingSpinner from 'src/common/components/loading-spinner';

export interface SlideOverBaseProps {
  title?: Nullable<string>;
  open: boolean;
  onOuterAreaClicked: () => void;
  onClose: () => void;
  maxWidth?: string;
  hideCloseIcon?: boolean;
  containerClassName?: string;
  titleClassName?: string;
  panelClassName?: string;
  children: ReactNode;
  actionLeftComponent?: ReactNode;
  showLoadingIndicator?: boolean;
}

const SlideOverBase = ({
  title,
  open,
  onClose,
  onOuterAreaClicked,
  maxWidth = 'lg',
  hideCloseIcon = false,
  containerClassName,
  titleClassName,
  panelClassName,
  children,
  actionLeftComponent,
  showLoadingIndicator = false,
}: SlideOverBaseProps) => {
  const maxWidthClass = classNames({
    'max-w-[368px]': maxWidth === 'xs',
    'max-w-screen-sm': maxWidth === 'sm',
    'max-w-screen-md': maxWidth === 'md',
    'max-w-screen-lg': maxWidth === 'lg',
    'max-w-screen-xl': maxWidth === 'xl',
    'max-w-full': maxWidth === 'full',
  });

  const actionBarClassName = classNames('flex', {
    'justify-end': !actionLeftComponent,
    'flex-row justify-between': actionLeftComponent,
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        static
        className={classNames('fixed inset-0 overflow-hidden', containerClassName)}
        open={open}
        onClose={onOuterAreaClicked}
      >
        <div className='absolute inset-0 overflow-hidden'>
          <Transition.Child
            as={Fragment}
            enter={`ease-in-out`}
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave={`ease-in-out`}
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='absolute inset-0 transition-opacity' />
          </Transition.Child>

          <div className='fixed inset-y-0 right-0 md:pl-10 max-w-full flex'>
            <Transition.Child
              as={Fragment}
              enter={`ease-in-out`}
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave={`ease-in-out`}
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div
                className={classNames(maxWidthClass, panelClassName)}
                style={{
                  boxShadow: '-1px 0 0 rgba(244, 244, 245, 1)',
                }}
              >
                <div className='h-full flex flex-col py-6 mt-16 md:mt-0 bg-white'>
                  <div className='px-4'>
                    <div className='flex flex-col space-y-4'>
                      <div className={actionBarClassName}>
                        {actionLeftComponent}
                        <div className='flex flex-row space-x-2'>
                          <div
                            className={classNames('w-6 h-6 flex items-center justify-center transition-opacity', {
                              'opacity-0': !showLoadingIndicator,
                              'opacity-100': showLoadingIndicator,
                            })}
                          >
                            <LoadingSpinner containerClassName='h-full' />
                          </div>
                          {!hideCloseIcon && <SlideOverCloseButton onClose={onClose} />}
                        </div>
                      </div>
                      {title && (
                        <Dialog.Title className={classNames('text-bedrock-black', titleClassName)}>
                          {title}
                        </Dialog.Title>
                      )}
                    </div>
                  </div>
                  <div className={classNames('relative flex-1 h-full overflow-y-auto px-4', !hideCloseIcon && 'mt-6')}>
                    {children}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SlideOverBase;
